import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { createElement } from 'react'
import { type SileoOptions, sileo } from 'sileo'

/**
 * Sileo's published `SileoOptions` type is missing the `id` field, but
 * its runtime `createToast` reads `id` and uses it for dedupe / update.
 * We extend the type locally so the rest of the file stays strict
 * without losing dedupe behaviour.
 */
type SileoOptionsWithId = SileoOptions & { id?: string }

/**
 * Outcome tones. Maps 1:1 onto Sileo's colored methods.
 *
 * - `success` — green. The action finished.
 * - `warning` — yellow. Pending, blocked, or a heads-up (not a failure).
 * - `error` — red. The action failed.
 *
 * Never call `sileo.show` — it defaults to `success` (green check). That is
 * how "DNS not verified" used to look done.
 */
export type ToastTone = 'success' | 'warning' | 'error'

/** Options shared by success / warning / error. */
export interface ToastOptions {
  /** Secondary line under the title. */
  description?: string
  /** Toast id — used to update an existing toast (e.g. loading → success). */
  id?: string | number
  /** How long the toast stays visible (ms). */
  duration?: number
  /** Optional action button rendered inside the toast card. */
  action?: { label: string; onClick: () => void }
  /** Override the default icon with a custom ReactNode. */
  icon?: ReactNode
}

/** Options for `appToast.loading` (no action button). */
export type LoadingToastOptions = Omit<ToastOptions, 'action'>

/** Options for `appToast.promise`. */
export interface PromiseToastOptions<T> {
  loading: string
  success: string | ((data: T) => string)
  error: string | ((err: unknown) => string)
  description?: string
}

function toSileoOpts(title: string, opts?: ToastOptions): SileoOptionsWithId {
  return {
    title,
    id: opts?.id !== undefined ? String(opts.id) : undefined,
    description: opts?.description,
    duration: opts?.duration ?? undefined,
    icon: opts?.icon ?? undefined,
    button: opts?.action
      ? { title: opts.action.label, onClick: opts.action.onClick }
      : undefined,
  }
}

function showToast(
  tone: ToastTone,
  title: string,
  opts?: ToastOptions
): string {
  return sileo[tone](toSileoOpts(title, opts))
}

export const appToast = {
  /** Green — the action finished. */
  success(title: string, opts?: ToastOptions): string {
    return showToast('success', title, opts)
  },

  /** Yellow — pending, blocked, or a heads-up. */
  warning(title: string, opts?: ToastOptions): string {
    return showToast('warning', title, opts)
  },

  /** Red — the action failed. */
  error(title: string, opts?: ToastOptions): string {
    return showToast('error', title, opts)
  },

  /** Yellow spinner, no auto-dismiss. Replace via the same `id`. */
  loading(title: string, opts?: LoadingToastOptions): string {
    return sileo.warning({
      ...toSileoOpts(title, opts),
      duration: opts?.duration ?? null,
      icon:
        opts?.icon ??
        createElement(Loader2, {
          className: 'size-4 animate-spin',
        }),
    })
  },

  /**
   * Promise-based toast flow: loading → success/error automatically.
   * Delegates to Sileo's native `sileo.promise` for timing.
   */
  promise<T>(promise: Promise<T>, opts: PromiseToastOptions<T>) {
    return sileo.promise(promise, {
      loading: { title: opts.loading },
      success:
        typeof opts.success === 'function'
          ? (data: T) => ({
              title: (opts.success as (d: T) => string)(data),
            })
          : { title: opts.success },
      error:
        typeof opts.error === 'function'
          ? (err: unknown) => ({
              title: (opts.error as (e: unknown) => string)(err),
            })
          : { title: opts.error },
      position: 'top-center',
    })
  },

  /** Dismiss a specific toast by id, or all toasts if no id given. */
  dismiss(id?: string | number): void {
    if (id !== undefined) {
      sileo.dismiss(String(id))
    } else {
      sileo.clear()
    }
  },
}
